import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { LEAD_STATUSES } from "../lib/data";
import { StatusBadge } from "../components/StatusBadge";
import { SourceBadge } from "../components/SourceBadge";
import { ArrowLeft, Save, Phone, Mail, MapPin, Package, Calendar } from "lucide-react";

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    api.get(`/admin/leads/${id}`).then((r) => {
      setLead(r.data);
      setStatus(r.data.status);
      setNotes(r.data.notes || "");
    });
  }, [id]);

  const save = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status, notes });
      setLead(data);
      setSavedMsg("Modifications enregistrées");
      setTimeout(() => setSavedMsg(""), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return <div className="container mx-auto px-6 py-20 text-center text-sm" data-testid="lead-loading">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-10">
      <div className="container mx-auto px-6 md:px-12 max-w-5xl">
        <Link to="/admin" className="text-xs uppercase tracking-widest text-[#5C5C5C] hover:text-[#1C1C1C] flex items-center gap-1 mb-6" data-testid="back-dashboard">
          <ArrowLeft className="w-3 h-3" /> Dashboard
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="eyebrow text-[#E07A5F]">Demande de devis</div>
              <SourceBadge source={lead.source_site} size="md" />
            </div>
            <h1 className="font-heading font-black text-4xl tracking-tighter">{lead.first_name} {lead.last_name}</h1>
            <p className="text-sm text-[#5C5C5C] mt-2">Reçue le {new Date(lead.created_at).toLocaleString("fr-FR")}</p>
          </div>
          <StatusBadge status={lead.status} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Section title="Coordonnées">
              <Row icon={Phone} label="Téléphone" value={<a href={`tel:${lead.phone}`} className="hover:text-[#E07A5F]" data-testid="lead-phone">{lead.phone}</a>} />
              <Row icon={Mail} label="Email" value={<a href={`mailto:${lead.email}`} className="hover:text-[#E07A5F]" data-testid="lead-email">{lead.email}</a>} />
            </Section>
            <Section title="Déménagement">
              <Row icon={MapPin} label="Départ" value={`${lead.departure_address} · ${lead.departure_city}`} />
              <Row icon={MapPin} label="Arrivée" value={`${lead.arrival_address} · ${lead.arrival_city}`} />
              <Row icon={Package} label="Volume" value={lead.volume} />
              <Row icon={Calendar} label="Date souhaitée" value={lead.moving_date} />
            </Section>
            {lead.message && (
              <Section title="Message">
                <p className="text-sm text-[#5C5C5C] leading-relaxed">{lead.message}</p>
              </Section>
            )}
            {lead.target_company_slug && (
              <Section title="Entreprise ciblée">
                <Link to={`/demenageur/${lead.target_company_slug}`} className="text-sm font-semibold text-[#1D3557] hover:text-[#E07A5F]" data-testid="lead-target">
                  {lead.target_company_slug} →
                </Link>
              </Section>
            )}
          </div>

          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-[#1C1C1C] shadow-hard-sm p-6">
              <div className="eyebrow text-[#5C5C5C] mb-4">Mettre à jour</div>
              <label className="block text-xs uppercase tracking-widest font-bold mb-2">Statut</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border border-[#E5E0D8] focus:border-[#1D3557] outline-none px-3 py-2 bg-white text-sm mb-4"
                data-testid="status-select"
              >
                {LEAD_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>

              <label className="block text-xs uppercase tracking-widest font-bold mb-2">Notes</label>
              <textarea
                rows={5}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border border-[#E5E0D8] focus:border-[#1D3557] outline-none px-3 py-2 bg-white text-sm mb-4"
                placeholder="Suivi, contacts effectués..."
                data-testid="notes-textarea"
              />

              <button onClick={save} disabled={saving} className="btn-primary w-full justify-center" data-testid="save-lead">
                <Save className="w-4 h-4" />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              {savedMsg && <div className="mt-3 text-xs text-[#81B29A]" data-testid="save-success">✓ {savedMsg}</div>}
            </div>
            <div className="text-xs text-[#5C5C5C] px-2 flex items-center gap-2">
              <span>Source :</span> <SourceBadge source={lead.source_site} size="xs" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-[#E5E0D8] p-6">
    <div className="eyebrow text-[#5C5C5C] mb-4">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="w-4 h-4 mt-0.5 text-[#5C5C5C] shrink-0" />
    <div>
      <div className="text-xs uppercase tracking-wider text-[#5C5C5C]">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  </div>
);
